import { useAuthStore } from '@/stores/auth.store';

/**
 * Check if the current user has a specific permission.
 * @param permission - Full permission key like 'jobs:edit'
 */
export function useHasPermission(permission: string): boolean {
  return useAuthStore((s) => s.permissions.includes(permission));
}

/**
 * Check if the current user has ALL of the specified permissions.
 */
export function useHasAllPermissions(...permissions: string[]): boolean {
  return useAuthStore((s) => permissions.every((p) => s.permissions.includes(p)));
}

/**
 * Check if the current user has ANY of the specified permissions.
 */
export function useHasAnyPermission(...permissions: string[]): boolean {
  return useAuthStore((s) => permissions.some((p) => s.permissions.includes(p)));
}

/**
 * Check resource access: useCanAccess('jobs', 'edit')
 */
export function useCanAccess(
  resource: string,
  action: 'view' | 'create' | 'edit' | 'delete',
): boolean {
  return useHasPermission(`${resource}:${action}`);
}

/**
 * Get the scope (OWN/TEAM/ALL) for a specific permission key.
 * Returns null if the permission is not granted.
 */
export function usePermissionScope(permission: string): string | null {
  return useAuthStore((s) => s.permissionScopes[permission] ?? null);
}

/**
 * Check if the current user has a specific capability flag.
 */
export function useHasCapability(flag: string): boolean {
  return useAuthStore((s) => !!s.capabilityFlags[flag]);
}

/**
 * Get the current user's field masks.
 */
export function useFieldMasks(): string[] {
  return useAuthStore((s) => s.fieldMasks);
}

/**
 * Returns true when the given mask category is active for the current user,
 * meaning the backend has stripped those fields from API responses and the
 * UI should hide the corresponding columns, cards, and inputs.
 *
 * @param mask - Uppercase mask category key, e.g. 'PRICING', 'PAYMENT_INFO'
 */
export function useIsFieldHidden(mask: string): boolean {
  return useAuthStore((s) => s.fieldMasks.includes(mask));
}
